import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone, Download } from "lucide-react";
import SEO, { productJsonLd, breadcrumbJsonLd } from "@/components/SEO";
import { ContactForm } from "@/components/Sections";
import { MODELS, BRAND } from "@/data/siteData";

export default function ModelDetailPage() {
    const { id } = useParams();
    const model = MODELS.find((m) => m.id === id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!model) return <Navigate to="/404" replace />;

    // Next 3 models in rotation (for "Drugi modeli" section)
    const idx = MODELS.findIndex((m) => m.id === id);
    const related = [
        MODELS[(idx + 1) % MODELS.length],
        MODELS[(idx + 2) % MODELS.length],
        MODELS[(idx + 3) % MODELS.length],
    ];

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            productJsonLd(model),
            breadcrumbJsonLd([
                { name: "Početna", path: "/" },
                { name: "Modeli", path: "/#modeli" },
                { name: model.name, path: `/modeli/${model.id}` },
            ]),
        ],
    };

    return (
        <>
            <SEO
                title={`${model.name} — ${model.tag}`}
                description={`Aluminijska ograda ${model.name}: ${model.desc} Tehničke specifikacije, dimenzije, RAL boje. Izrada po mjeri u BiH.`}
                path={`/modeli/${model.id}`}
                image={model.img}
                type="product"
                keywords={`aluminijska ograda ${model.name.toLowerCase()}, ${model.tag.toLowerCase()}, alu ograde, ograde Tuzla, ograde BiH, RAL boje`}
                jsonLd={jsonLd}
            />

            {/* Hero — text left, image right */}
            <section className="pt-[120px] pb-12 lg:pt-[160px] lg:pb-20 border-b border-[hsl(var(--line))]">
                <div className="container-p">
                    <nav className="flex items-center gap-2 text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--muted-ink))]" aria-label="Breadcrumbs" data-testid="breadcrumbs">
                        <Link to="/" className="hover:text-[hsl(var(--accent))]">Početna</Link>
                        <span>/</span>
                        <Link to="/#modeli" className="hover:text-[hsl(var(--accent))]">Modeli</Link>
                        <span>/</span>
                        <span className="text-[hsl(var(--ink))]">{model.name}</span>
                    </nav>

                    <div className="mt-10 grid items-start gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <span className="eyebrow">Model · Aluminijska ograda</span>
                            <h1 className="mt-6 font-[Cabinet_Grotesk] text-[clamp(3.2rem,9vw,7rem)] font-black leading-[0.92] uppercase">
                                {model.name}<span className="text-[hsl(var(--accent))]">.</span>
                            </h1>
                            <p className="mt-4 text-[0.78rem] font-bold uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
                                {model.tag}
                            </p>

                            <p className="mt-7 max-w-xl text-[1.02rem] leading-[1.7] text-[hsl(var(--muted-ink))]">
                                {model.long || model.desc}
                            </p>

                            <ul className="mt-8 space-y-3">
                                {(model.bullets || []).map((b) => (
                                    <li key={b} className="flex items-start gap-3" data-testid="model-bullet">
                                        <Check className="mt-1 h-5 w-5 shrink-0 text-[hsl(var(--accent))]" strokeWidth={2.5} />
                                        <span className="text-[hsl(var(--ink))] leading-relaxed">{b}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-10 flex flex-wrap gap-3">
                                <a href="#kontakt" className="btn btn-accent" data-testid="model-cta-quote">
                                    Ponuda za {model.name} <ArrowRight className="h-4 w-4" />
                                </a>
                                <Link to="/#modeli" className="btn btn-ghost" data-testid="model-cta-all">
                                    Svi modeli
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="relative bg-gradient-to-br from-[#f7f7f8] to-[#dedde0] aspect-[4/5] overflow-hidden">
                                <span className="corner-tl" aria-hidden />
                                <span className="corner-br" aria-hidden />
                                <img
                                    src={model.img}
                                    alt={`Aluminijska ograda ${model.name} — ${model.tag}`}
                                    className="h-full w-full object-contain p-6"
                                />
                                <div className="absolute bottom-6 left-6 bg-[hsl(var(--ink))] px-5 py-2.5 text-xs font-bold tracking-[0.16em] text-white">
                                    {model.name}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tehnički podaci — clean 2-col grid */}
            <section className="py-20 lg:py-28">
                <div className="container-p">
                    <div className="grid gap-14 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <span className="eyebrow">Tehnički podaci</span>
                            <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.2rem)] leading-[0.98]">
                                Kvalitet. <br />
                                <span className="text-[hsl(var(--accent))]">Promišljen.</span>
                            </h2>
                            <p className="mt-6 max-w-md text-[hsl(var(--muted-ink))] leading-[1.7]">
                                Detaljne specifikacije modela {model.name}. Sve dimenzije su standardne — po dogovoru radimo i nestandardne mjere prema vašem projektu.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <a href={BRAND.phoneHref} className="btn btn-primary" data-testid="model-cta-phone">
                                    <Phone className="h-4 w-4" /> {BRAND.phone}
                                </a>
                                <Link to="/katalog" className="btn btn-ghost">
                                    <Download className="h-4 w-4" /> Katalog (PDF)
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            <dl className="border-t-2 border-[hsl(var(--ink))]">
                                {(model.techData || []).map(([k, v]) => (
                                    <div key={k} className="grid grid-cols-12 gap-4 border-b border-[hsl(var(--line))] py-5" data-testid="model-tech-row">
                                        <dt className="col-span-12 sm:col-span-5 text-[0.78rem] font-bold uppercase tracking-[0.14em] text-[hsl(var(--muted-ink))]">{k}</dt>
                                        <dd className="col-span-12 sm:col-span-7 text-[1rem] font-semibold text-[hsl(var(--ink))]">{v}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </div>
                </div>
            </section>

            {/* Reference — model-specific gallery */}
            {model.gallery && model.gallery.length > 0 && (
                <section className="py-20 lg:py-28 bg-[hsl(var(--paper-2))] border-t border-[hsl(var(--line))]">
                    <div className="container-p">
                        <div className="grid items-end gap-10 lg:grid-cols-12">
                            <div className="lg:col-span-6">
                                <span className="eyebrow">Reference</span>
                                <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.2rem)] leading-[0.98]">
                                    Model {model.name}<br />
                                    <span className="text-[hsl(var(--accent))]">u praksi.</span>
                                </h2>
                            </div>
                            <p className="lg:col-span-6 text-[hsl(var(--muted-ink))] leading-[1.7]">
                                Realizovani projekti sa modelom {model.name} — od porodičnih kuća do poslovnih objekata. Svaka instalacija se izrađuje individualno po mjeri.
                            </p>
                        </div>

                        <div className="mt-12 grid gap-6 sm:grid-cols-2 max-w-5xl mx-auto">
                            {model.gallery.map((g, i) => (
                                <div key={i} className="tile" style={{ aspectRatio: "1/1" }} data-testid={`model-gallery-${i}`}>
                                    <img src={g.src} alt={g.alt} loading="lazy" />
                                    <span className="label">{model.name} · {i + 1}/{model.gallery.length}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA banner */}
            <section className="band-dark py-20">
                <div className="container-p">
                    <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
                        <div>
                            <span className="eyebrow" style={{ color: "#fff" }}>Sljedeći korak</span>
                            <h2 className="mt-4 text-[clamp(1.8rem,3.6vw,2.8rem)] leading-[1]">
                                Spremni za <span className="text-[hsl(var(--accent))]">{model.name}</span>?
                            </h2>
                            <p className="mt-4 max-w-xl text-[rgba(255,255,255,0.72)] leading-relaxed">
                                Pošaljite okvirne mjere — u roku od 24h dobit ćete besplatnu i neobavezujuću ponudu.
                            </p>
                        </div>
                        <a href="#kontakt" className="btn btn-accent">
                            Zatraži ponudu <ArrowRight className="h-4 w-4" />
                        </a>
                    </div>
                </div>
            </section>

            {/* Drugi modeli */}
            <section className="py-20 lg:py-28 border-t border-[hsl(var(--line))]">
                <div className="container-p">
                    <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <span className="eyebrow">Drugi modeli</span>
                            <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.2rem)] leading-[0.98]">
                                Možda vas <br />
                                <span className="text-[hsl(var(--accent))]">zanima i.</span>
                            </h2>
                        </div>
                        <Link to="/#modeli" className="btn btn-ghost self-start lg:self-auto" data-testid="model-related-all">
                            Svi modeli <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {related.map((rm) => (
                            <Link
                                to={`/modeli/${rm.id}`}
                                key={rm.id}
                                className="model-card block"
                                data-testid={`model-related-${rm.id}`}
                            >
                                <div className="thumb">
                                    <img src={rm.img} alt={`Aluminijska ograda ${rm.name}`} loading="lazy" />
                                    <span className="nr">{rm.n}</span>
                                    <span className="name-tag">{rm.name}</span>
                                </div>
                                <div className="p-6">
                                    <p className="text-[0.76rem] font-bold uppercase tracking-[0.14em] text-[hsl(var(--accent))]">
                                        {rm.tag}
                                    </p>
                                    <h3 className="mt-2 font-[Cabinet_Grotesk] text-[1.4rem] font-black tracking-tight uppercase leading-tight">
                                        {rm.name}
                                    </h3>
                                    <span className="mt-4 inline-flex items-center gap-2 text-[0.76rem] font-bold uppercase tracking-[0.16em] text-[hsl(var(--ink))]">
                                        Detalji <ArrowRight className="h-4 w-4" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <ContactForm defaultProduct={model.name} />
        </>
    );
}
